'use client';

import { ReactNode } from 'react';

import { EkranBledu } from '../marka/EkranBledu';
import { EkranStartowy } from '../marka/EkranStartowy';

/**
 * Ekran bramki wejścia do organu — jeden wygląd dla wszystkich aplikacji
 * modułowych.
 *
 * Decyzję podejmuje `useDostepDoOrganu` z `@insolitum/supabase` (jedno
 * wywołanie `ma_dostep`, migracja 674); ten komponent tylko ją rysuje. Podział
 * jest celowy: gdyby każda aplikacja miała własny ekran odmowy, miałaby też
 * własną odpowiedź na pytanie „co znaczy brak dostępu" — a stąd już blisko do
 * własnej reguły.
 *
 * PIĘĆ stanów, nie dwa. Najważniejsza para to `blad` i `brak-dostepu`:
 * „nie udało się zapytać" nie jest tym samym co „nie masz tego organu",
 * a zlanie ich w jeden komunikat uczy człowieka, że napis „moduł nieaktywny"
 * bywa nieprawdą — i wtedy przestaje mu wierzyć także wtedy, gdy jest prawdą.
 *
 * Ten sam podział niesie znak Insolitum: `sprawdzam` to sieć przy pracy
 * (`EkranStartowy`), `blad` to organy odcięte od sieci (`EkranBledu`,
 * wariant `polaczenie`), a trzy odmowy — brak sesji, organizacji, organu —
 * NIE są rozsypane, bo nic się nie zepsuło: system działa, a odpowiedź jest
 * prawdziwa. Odmowa to ten sam ekran jednego zadania co awaria (`EkranBledu
 * wariant="brak-dostepu"`: ten sam rytm, ta sama oś, podpis 24 px od dołu),
 * tylko jej postacią jest KAFEL ORGANU, nie znak — „kafel to organ, goły
 * znak to Insolitum". Aplikacja podaje swój kafel (`kafel`); bez niego
 * odmowa stoi bez postaci, a znak Insolitum zostaje w podpisie.
 */
// =============================================================================
// Nazwa organu dla człowieka — jedna dla całej platformy.
//
// DLACZEGO TU, a nie w `@insolitum/types`, gdzie mieszka lista organów:
// `packages/types/src/index.ts` re-eksportuje 52 moduły z rozszerzeniem `.js`
// (konwencja NodeNext). `tsc` mapuje `./user.js` na `./user.ts` i jest
// zadowolony, ale **webpack Next.js tego nie robi** — więc pobranie z tego
// pakietu czegokolwiek, co istnieje w RUNTIME, wywala `next build`
// komunikatem `Module not found: Can't resolve './user.js'`. Zmierzone
// 04.09.2026: `module-neurosense` i `module-nexus` padły dokładnie tak, gdy
// adapter bramki zaimportował stąd stałą.
//
// Pułapka jest w tym, że aplikacje modułowe importują `@insolitum/types` od
// dawna i wszystko działa — bo importują z niego wyłącznie TYPY, a te są
// wymazywane przed webpackiem. Pierwszy import WARTOŚCI odsłonił problem.
//
// Poprawianie tego u źródła (zdjęcie 52 sufiksów `.js` w pakiecie, od którego
// zależy 52 przestrzenie roboczej — a tę samą konwencję ma jeszcze pięć
// innych pakietów) to zmiana o zasięgu nieproporcjonalnym do naprawianej
// usterki. Nazwa organu jest natomiast tam, gdzie jej naprawdę potrzeba:
// obok ekranu, który ją wypisuje.
//
// Te same napisy nosi rejestr powłoki (`apps/universe-shell/src/modules/
// registry.ts`, pole `displayName`) — kafel w pasku bocznym i nazwa organu na
// ekranie muszą nazywać ten sam organ tak samo, inaczej człowiek szuka
// w panelu kafla o innej nazwie. Zgodność z rejestrem ORAZ pokrycie listy
// organów (`ORGANY_PLATFORMY`) pilnuje
// `tests/unit/apps/dostepDoOrganuWAplikacjach.test.ts`.
//
// W ZDANIU nazwa stoi bez „INSOLITUM" i bez wersalików (`NAZWY_W_ZDANIU`):
// „INSOLITUM AGENDA" wpisane krojem interfejsu na ekranie, którego podpisem
// jest znak Insolitum, to fałszywy napis obok prawdziwego. Mapa jest jawna,
// a nie wyliczana regułą, bo reguła zrobiłaby „Neurosense Iot" i „Ucs
// Shipping"; zgodność obu map co do liter pilnuje
// `tests/unit/marka/nazwyOrganowWZdaniu.test.ts`.
// =============================================================================

export const NAZWY_ORGANOW: Record<string, string> = {
  agenda: 'INSOLITUM AGENDA',
  artifex: 'INSOLITUM ARTIFEX',
  conspectus: 'INSOLITUM NEXUS CONSPECTUS',
  fabrica: 'INSOLITUM FABRICA',
  furnus: 'INSOLITUM FURNUS',
  gamification: 'INSOLITUM NEXUS AEGIS',
  lexshift: 'INSOLITUM LEXSHIFT',
  neurosense: 'INSOLITUM NEUROSENSE IOT',
  nexus: 'INSOLITUM NEXUS',
  print: 'INSOLITUM CLOUD PRINT',
  provisio: 'INSOLITUM PROVISIO',
  ucs: 'UCS SHIPPING',
  waterandice: 'INSOLITUM TEMPERATIO',
};

/** Nazwa organu dla człowieka; identyfikator, gdy organ nieznany. */
export function nazwaOrganu(organ: string): string {
  return NAZWY_ORGANOW[organ] ?? organ;
}

/** Nazwa organu w zdaniu — bez „INSOLITUM", wielkość liter jak w nazwie własnej. */
export const NAZWY_W_ZDANIU: Record<string, string> = {
  agenda: 'Agenda',
  artifex: 'Artifex',
  conspectus: 'Nexus Conspectus',
  fabrica: 'Fabrica',
  furnus: 'Furnus',
  gamification: 'Nexus Aegis',
  lexshift: 'LexShift',
  neurosense: 'NeuroSense IoT',
  nexus: 'Nexus',
  print: 'Cloud Print',
  provisio: 'Provisio',
  ucs: 'UCS Shipping',
  waterandice: 'Temperatio',
};

/** Nazwa organu do zdania; identyfikator, gdy organ nieznany. */
export function nazwaWZdaniu(organ: string): string {
  return NAZWY_W_ZDANIU[organ] ?? organ;
}

// =============================================================================
// Zdania odmowy „brak sesji" — jedne dla bramki i dla AuthGuardów organów.
//
// Ten sam stan (brak sesji) w tym samym organie stwierdzają DWA strażniki:
// AuthGuard aplikacji (wejście z bezpośredniego adresu) i ta bramka (wejście
// z ramki powłoki). Do 05.10.2026 każdy miał własne zdania na identycznym
// ekranie: AuthGuard mówił „Brak dostępu" i „modułu Agenda", bramka —
// „Zaloguj się" i „organu Agenda". Tytuł „Brak dostępu" nazywał przy tym brak
// sesji ODMOWĄ, a to jest inny stan (`brak-dostepu`: organizacja nie ma
// organu). Człowiek z wygasłą sesją widział raz jeden, raz drugi tekst,
// zależnie od tego, który strażnik zdążył pierwszy.
//
// Aplikacja z tłumaczeniami (Furnus) trzyma te zdania w swoich plikach
// językowych; wersja polska ma brzmieć dokładnie tak samo — pilnuje tego
// `tests/unit/marka/odmowaBrakuSesji.test.ts`.
// =============================================================================

export interface ZdaniaBrakuSesji {
  /** „Organ Agenda" — nazwa organu stoi w nadtytule, nagłówek jej nie powtarza. */
  readonly nadtytul: string;
  readonly tytul: string;
  readonly tresc: string;
  /** Etykieta jedynej akcji (link do logowania w powłoce). */
  readonly akcja: string;
}

/**
 * Nadtytuł każdego ekranu organu („Organ Agenda") — jedno zdanie dla odmowy
 * braku sesji, organizacji i organu: w bramce, w AuthGuardach i w powłoce.
 *
 * Przyjmuje nazwę JUŻ rozwiązaną, nie identyfikator: powłoka nazywa tak także
 * moduł partnera, którego nie ma w `NAZWY_W_ZDANIU` (nazwa z wiersza
 * `modules`). Organ platformy podaje `nazwaWZdaniu(organ)`.
 */
export function nadtytulOrganu(nazwa: string): string {
  return `Organ ${nazwa}`;
}

/** Zdania ekranu „brak sesji" dla organu (`modules.id`). */
export function zdaniaBrakuSesji(organ: string): ZdaniaBrakuSesji {
  const nazwa = nazwaWZdaniu(organ);
  return {
    nadtytul: nadtytulOrganu(nazwa),
    tytul: 'Zaloguj się',
    tresc: `Dostęp do organu ${nazwa} wymaga zalogowania w Insolitum Universe.`,
    akcja: 'Przejdź do logowania',
  };
}

export type StanBramkiOrganu =
  | 'sprawdzam'
  | 'brak-sesji'
  | 'brak-organizacji'
  | 'brak-dostepu'
  | 'blad'
  | 'ma-dostep';

export interface EkranBramkiOrganuProps {
  stan: StanBramkiOrganu;
  /** Identyfikator organu (`modules.id`). Nazwę dla człowieka ekran zna sam. */
  organ: string;
  /** Powtórzenie pytania — pokazywane tylko przy stanie `blad`. */
  sprawdzPonownie?: () => void;
  /** Adres powłoki; domyślnie produkcyjny. */
  adresPowloki?: string;
  /**
   * Kafel organu (`SKALA_ZNAKU.kafelOrganu`, 64 px) — postać ekranów odmowy.
   * Bez niego odmowa stoi bez postaci (tekst od nadtytułu).
   */
  kafel?: ReactNode;
  children: ReactNode;
}

export function EkranBramkiOrganu({
  stan,
  organ,
  sprawdzPonownie,
  adresPowloki = 'https://universe.insolitum.ai',
  kafel,
  children,
}: EkranBramkiOrganuProps) {
  if (stan === 'ma-dostep') return <>{children}</>;

  // Nazwa jest wyprowadzana z identyfikatora, a nie podawana przez aplikację:
  // dziesięć aplikacji podających własny literał to dziesięć okazji na
  // rozjazd z kaflem w powłoce.
  const nazwa = nazwaWZdaniu(organ);

  if (stan === 'sprawdzam') {
    // Ten sam ekran co w AuthGuard tuż przed bramką: znak pionowy stoi w tym
    // samym miejscu, a pod nim TA SAMA nazwa organu (`nazwaWZdaniu`, jak
    // w każdym AuthGuardzie) — zmienia się tylko komunikat dla czytnika
    // ekranu. Bez nazwy napis „AGENDA” znikał pod znakiem na czas zapytania
    // `ma_dostep`, czyli migał przy każdym otwarciu organu. Zgodność obu
    // ekranów pilnuje `tests/unit/marka/nazwyOrganowWZdaniu.test.ts`.
    return (
      <EkranStartowy
        uklad="pelny"
        aria-busy="true"
        nazwa={nazwa}
        komunikat={`Sprawdzanie dostępu do organu ${nazwa}…`}
      />
    );
  }

  if (stan === 'blad') {
    // Bramka owija cały layout organu, więc nad nią nie ma paska bocznego —
    // ekran stoi na całej wysokości (`pelny`), z tą samą kotwicą znaku co
    // każdy inny ekran stanu.
    return (
      <EkranBledu
        wariant="polaczenie"
        uklad="pelny"
        // Nadtytuł wariantu („Brak połączenia") zakładałby przyczynę, której
        // nie znamy — wiemy tylko, że odpowiedź nie przyszła.
        nadtytul="Brak odpowiedzi"
        tytul="Nie udało się sprawdzić dostępu"
        // Świadomie NIE piszemy „brak dostępu": nie wiemy tego.
        tresc={
          <p>
            Nie dostaliśmy odpowiedzi z serwera, więc nie wiemy, czy Twoja organizacja ma organ{' '}
            {nazwa}. To nie znaczy, że go nie ma.
          </p>
        }
        akcje={
          sprawdzPonownie && (
            <button type="button" className="btn btn--primary btn--lg" onClick={sprawdzPonownie}>
              Spróbuj ponownie
            </button>
          )
        }
      />
    );
  }

  // Trzy odmowy: jeden układ, różne zdania. Nazwa organu stoi w nadtytule,
  // więc nagłówki jej nie powtarzają. Nadtytuł przychodzi z jednego miejsca
  // (`nadtytulOrganu`, przez `zdaniaBrakuSesji` dla braku sesji) — ekran nie
  // składa go po swojemu, bo druga kopia zdania rozjeżdża się jak pierwsza.
  const odmowa = (nadtytul: string, tytul: string, tresc: ReactNode, akcja: ReactNode) => (
    <EkranBledu
      wariant="brak-dostepu"
      uklad="pelny"
      ilustracja={kafel ?? null}
      nadtytul={nadtytul}
      tytul={tytul}
      tresc={<p>{tresc}</p>}
      akcje={akcja}
    />
  );

  if (stan === 'brak-sesji') {
    const zdania = zdaniaBrakuSesji(organ);
    return odmowa(
      zdania.nadtytul,
      zdania.tytul,
      zdania.tresc,
      <a href={adresPowloki} className="btn btn--primary btn--lg">
        {zdania.akcja}
      </a>
    );
  }

  if (stan === 'brak-organizacji') {
    return odmowa(
      nadtytulOrganu(nazwa),
      'Nie wiemy, o którą organizację pytać',
      <>
        Twoje konto nie jest przypisane do żadnej czynnej organizacji, albo adres tej strony został
        otwarty bez kontekstu organizacji. Wejdź w organ {nazwa} z panelu Universe.
      </>,
      <a href={adresPowloki} className="btn btn--primary btn--lg">
        Otwórz panel Universe
      </a>
    );
  }

  return odmowa(
    nadtytulOrganu(nazwa),
    'Organ nie jest aktywny',
    <>
      Twoja organizacja nie ma czynnej subskrypcji organu {nazwa}. Administrator może go włączyć
      albo uruchomić okres próbny w katalogu organów.
    </>,
    <a href={`${adresPowloki}/admin/modules`} className="btn btn--primary btn--lg">
      Katalog organów
    </a>
  );
}
